/**
 * その日の睡眠1回ずつの内訳（睡眠段階・主な指標・寝る前の過ごし方）
 */

import { useMemo } from 'react'

import type { SleepDayEpisode } from '@/api/types/sleep'
import type { SleepLevel } from '@/pages/activity/components/sleep-graph'
import { SleepGraph } from '@/pages/activity/components/sleep-graph'

import { formatMinutes, formatSleepRange, QUALIFIER_LABEL, SLEEP_KIND_LABEL, SUB_QUALIFIER_LABEL } from '../format'
import { PreSleepDetails } from './pre-sleep-card'
import { RawDataSection } from './raw-data-section'

type Props = {
  episodes: SleepDayEpisode[]
  usualPlaceName: string | null
}

const Stat = ({ label, value }: { label: string; value: string | null }) =>
  value === null ? null : (
    <span className="text-[11px] text-muted-foreground">
      {label} <span className="font-medium text-foreground tabular-nums">{value}</span>
    </span>
  )

const pct = (v: number | null) => (v === null ? null : `${v.toFixed(0)}%`)

const EpisodeCard = ({ episode, usualPlaceName }: { episode: SleepDayEpisode; usualPlaceName: string | null }) => {
  const { summary, metrics: m } = episode
  const levels: SleepLevel[] = useMemo(
    () => episode.levels.map((l) => ({ startTime: l.start_time, endTime: l.end_time, level: l.level })),
    [episode.levels],
  )
  const subQualifiers = Object.entries(m.sub_qualifiers)

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <p className="text-[11px] text-muted-foreground">
            {SLEEP_KIND_LABEL.get(summary.kind) ?? summary.kind}
            {summary.source === 'snapshot' && '（Garmin で上書きされる前の記録）'}
          </p>
          <p className="text-sm font-medium tabular-nums">{formatSleepRange(summary.sleep_start, summary.sleep_end)}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold tabular-nums">{formatMinutes(m.sleep_min)}</p>
          {m.score !== null && (
            <p className="text-[11px] text-indigo-600 dark:text-indigo-300">
              スコア {m.score}
              {m.score_qualifier && `（${QUALIFIER_LABEL.get(m.score_qualifier) ?? m.score_qualifier}）`}
            </p>
          )}
        </div>
      </div>

      {levels.length > 0 && (
        <SleepGraph
          startTime={summary.sleep_start}
          endTime={summary.sleep_end}
          levels={levels}
          stages={{
            deep: (m.deep_min ?? 0) * 60,
            light: (m.light_min ?? 0) * 60,
            rem: (m.rem_min ?? 0) * 60,
            awake: (m.awake_min ?? 0) * 60,
          }}
        />
      )}

      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <Stat label="深い" value={pct(m.deep_pct)} />
        <Stat label="REM" value={pct(m.rem_pct)} />
        <Stat label="効率" value={pct(m.efficiency_pct)} />
        <Stat label="最低心拍" value={m.hr_min !== null ? `${m.hr_min}` : null} />
        <Stat label="HRV" value={m.hrv_avg !== null ? `${m.hrv_avg.toFixed(0)}ms` : null} />
        <Stat label="BB" value={m.bb_gain !== null ? `+${m.bb_gain}` : null} />
        <Stat label="ストレス" value={m.stress_avg !== null ? m.stress_avg.toFixed(0) : null} />
        <Stat label="SpO2 平均" value={pct(m.spo2_avg)} />
        <Stat label="SpO2 最低" value={pct(m.spo2_low)} />
        <Stat label="呼吸数" value={m.resp_avg !== null ? `${m.resp_avg.toFixed(0)}回/分` : null} />
      </div>

      {subQualifiers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {subQualifiers.map(([key, q]) => (
            <span key={key} className="rounded-full border px-2 py-0.5 text-[11px]">
              {SUB_QUALIFIER_LABEL.get(key) ?? key}: {QUALIFIER_LABEL.get(q) ?? q}
            </span>
          ))}
        </div>
      )}

      <details className="group">
        <summary className="cursor-pointer text-xs text-muted-foreground">寝る前の過ごし方</summary>
        <PreSleepDetails metrics={m} usualPlaceName={usualPlaceName} />
      </details>
      <RawDataSection episodeId={summary.id} />
    </div>
  )
}

export const EpisodeList = ({ episodes, usualPlaceName }: Props) => (
  <section className="space-y-2 rounded-xl border bg-card p-4 shadow-sm">
    <p className="text-sm font-medium">{episodes.length > 1 ? `睡眠の内訳（${episodes.length}回）` : '睡眠の内訳'}</p>
    {episodes.map((e) => (
      <EpisodeCard key={e.summary.id} episode={e} usualPlaceName={usualPlaceName} />
    ))}
  </section>
)
